import { apiClient } from './client'
import type {
  AgentJobActions,
  AgentJobCostResponse,
  AgentJobDetailResponse,
  AgentJobInitiatorKind,
  AgentJobEventsResponse,
  AgentJobListResponse,
  AgentJobSnapshot,
  AgentJobApproval,
  AgentJobArtifact,
  AgentJobProvenance,
  AgentJobResult,
} from '@/types/agentJob'

export const DETERMINISTIC_OUTPUT_RETURN_CONTRACT = {
  format: 'json',
  schema_ref: 'https://contracts.agentmaurice.app/agent-jobs/v2/schemas/results/deterministic-output.schema.json',
  schema_sha256: '143c87ea16f6d2c81d9d7efacc4dff54b755515074bb2c378259645513fe1574',
  on_invalid: 'blocked',
  max_validation_failures: 1,
} as const

// Normalisation défensive du détail (AGENT-812) : un ancien serveur n'envoie ni
// `provenance` ni `approvals`. Les approbations deviennent `[]`, la provenance reste
// indéfinie, et une entrée d'approbation sans `action_id` est écartée.
function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

export function normalizeAgentJobDetail(response: AgentJobDetailResponse): AgentJobDetailResponse {
  const raw: unknown = response
  const source = isRecord(raw) ? raw : {}
  const approvals: AgentJobApproval[] = Array.isArray(source.approvals)
    ? source.approvals.filter((item): item is AgentJobApproval => isRecord(item) && typeof item.action_id === 'string')
    : []
  const provenance = isRecord(source.provenance) ? (source.provenance as AgentJobProvenance) : undefined
  return { ...response, provenance, approvals }
}

// Sans Application (`applicationId` null ou absent), le job est lu sur les routes
// organisation : c'est le cas d'un job créé par un plan d'exécution en portée organisation.
function jobsPath(organizationId: string, applicationId?: string | null) {
  const organizationPath = `/v2/organizations/${encodeURIComponent(organizationId)}`
  if (!applicationId) return `${organizationPath}/jobs`
  return `${organizationPath}/applications/${encodeURIComponent(applicationId)}/jobs`
}

export const agentJobsAPI = {
  create(organizationId: string, applicationId: string, input: {
    agent_id: string
    workflow_id: string
    workflow_version: string
    goal: string
    inputs?: Record<string, unknown>
    return_contract: { format: string; schema_ref: string; schema_sha256: string; on_invalid: string; max_validation_failures: number }
  }) {
    return apiClient.post<{ job: AgentJobSnapshot; created: boolean }>(jobsPath(organizationId, applicationId), { ...input, idempotency_key: crypto.randomUUID() })
  },
  // `initiatorKind` filtre côté serveur sur l'initiateur stocké (AGENT-794).
  list(organizationId: string, applicationId: string, options?: { initiatorKind?: AgentJobInitiatorKind | null }) {
    if (options?.initiatorKind) {
      return apiClient.get<AgentJobListResponse>(jobsPath(organizationId, applicationId), { params: { initiator_kind: options.initiatorKind } })
    }
    return apiClient.get<AgentJobListResponse>(jobsPath(organizationId, applicationId))
  },

  async get(organizationId: string, applicationId: string | null | undefined, jobId: string) {
    const response = await apiClient.get<AgentJobDetailResponse>(
      `${jobsPath(organizationId, applicationId)}/${encodeURIComponent(jobId)}`,
    )
    return normalizeAgentJobDetail(response)
  },

  result(organizationId: string, applicationId: string | null | undefined, jobId: string) {
    return apiClient.get<{ result: AgentJobResult | null }>(
      `${jobsPath(organizationId, applicationId)}/${encodeURIComponent(jobId)}/result`,
    )
  },

  artifacts(organizationId: string, applicationId: string | null | undefined, jobId: string) {
    return apiClient.get<{ artifacts: AgentJobArtifact[] }>(
      `${jobsPath(organizationId, applicationId)}/${encodeURIComponent(jobId)}/artifacts`,
    )
  },

  cost(organizationId: string, applicationId: string | null | undefined, jobId: string) {
    return apiClient.get<AgentJobCostResponse>(
      `${jobsPath(organizationId, applicationId)}/${encodeURIComponent(jobId)}/cost`,
    )
  },

  events(
    organizationId: string,
    applicationId: string | null | undefined,
    jobId: string,
    cursor?: { sequence?: number; event_id?: string },
  ) {
    return apiClient.get<AgentJobEventsResponse>(
      `${jobsPath(organizationId, applicationId)}/${encodeURIComponent(jobId)}/events`,
      { params: { sequence: cursor?.sequence ?? 0, event_id: cursor?.event_id ?? '' } },
    )
  },

  cancel(organizationId: string, applicationId: string | null | undefined, jobId: string) {
    return apiClient.post<{ job: AgentJobSnapshot }>(
      `${jobsPath(organizationId, applicationId)}/${encodeURIComponent(jobId)}/cancel`,
      {},
    )
  },

  retry(organizationId: string, applicationId: string | null | undefined, jobId: string) {
    return apiClient.post<{ job: AgentJobSnapshot; created: boolean }>(
      `${jobsPath(organizationId, applicationId)}/${encodeURIComponent(jobId)}/retry`,
      { idempotency_key: crypto.randomUUID() },
    )
  },
}

export type { AgentJobActions }
